import React from 'react'
import {
  ContentSection,
  DescriptionList,
  Layout,
  Paragraph,
  Button,
  Heading,
} from '@vwfs-bronson/bronson-react'
import { useTranslation } from 'react-i18next'
import { Output } from '../../../Text'
import summaryTexts from '../../../resources/base/content/summary.json'
import './style.css'
import { LegalFormType } from '../../../models/enums'
import { getPartnerType, isVehicleDeliveryTypeDealerShop } from '../../../services/common/utils'

const SummarySection = (props) => {
  const { t, i18n } = useTranslation()
  const { isBusiness, isVtiGis, getValues, storefrontData } = props
  const partnerType = getPartnerType()
  const isNotDeliveryTypeDealershop = !isVehicleDeliveryTypeDealerShop(storefrontData)
  const getLegalFormText = (legalFormType: LegalFormType) => {
    return i18n.exists(
      `personal-details:entityLegalForm.label.${legalFormType}`
    )
      ? t(`personal-details:entityLegalForm.label.${legalFormType}`)
      : ''
  }

  const data = {
    "Firmenname": getValues?.customerData?.companyName,
    "Firma": getValues?.companyData?.name,
    "Rechtsform": getLegalFormText(getValues?.companyData?.entityLegalForm),
    "Vorname(n)": getValues?.customerData?.firstName,
    "Nachname(n)": getValues?.customerData?.lastName,
    "Straße": getValues?.customerData?.address?.street,
    "Hausnummer": getValues?.customerData?.address?.houseNumber,
    "Postleitzahl": getValues?.customerData?.address?.zipCode,
    "Ort": getValues?.customerData?.address?.city,
    "Geburtsdatum": getValues?.customerData?.dateOfBirth,
    "E-Mail-Adresse": getValues?.customerData?.contactData?.email,
    "Mobilfunknummer": getValues?.customerData?.contactData?.mobilePhoneCountryCode  && getValues?.customerData?.contactData?.mobilePhoneNumber
      ? `${getValues?.customerData?.contactData?.mobilePhoneCountryCode} ${getValues?.customerData?.contactData?.mobilePhoneNumber}`
      : '',
    "deliveryAddress.Vorname(n)": getValues?.customerData?.deliveryAddress?.deviatingFirstName,
    "deliveryAddress.Nachname(n)": getValues?.customerData?.deliveryAddress?.deviatingLastName,
    "deliveryAddress.Straße": getValues?.customerData?.deliveryAddress?.street,
    "deliveryAddress.Hausnummer": getValues?.customerData?.deliveryAddress?.houseNumber,
    "deliveryAddress.Postleitzahl": getValues?.customerData?.deliveryAddress?.zipCode,
    "deliveryAddress.Ort": getValues?.customerData?.deliveryAddress?.city,
    "deliveryAddress.Land": getValues?.customerData?.deliveryAddress?.countryCode,
    ...(getValues?.dkxNumber && { "DKX-Kennung": getValues?.dkxNumber })
  }

  const DescriptionListBase = () => {
    const descriptionListTitles = Output.resolve(
      `${isBusiness ? `descriptionListBusiness` : `descriptionList`}`,
      summaryTexts
    )
    return (
      <Layout>
        {descriptionListTitles?.items?.length > 0 && (
          <Layout.Item default="1/1">
            <DescriptionList grid>
              {descriptionListTitles &&
                descriptionListTitles?.items?.map(
                  (item: string) => (
                    data?.[item]
                    ? (
                      <DescriptionList.Group key={item} termText={item}>
                        <DescriptionList.Detail>
                          {data?.[item]}
                        </DescriptionList.Detail>
                      </DescriptionList.Group>
                    ) : <></>
                  )
                )}
            </DescriptionList>
          </Layout.Item>
        )}
      </Layout>
    )
  }

  const DescriptionListVtiGis = () => {
    const partnerTypeItems = t(`summary:descriptionListVtiGis:${partnerType}`, {returnObjects: true}) as any
    const isActiveDeliveryAddress = !!getValues?.customerData?.deliveryAddressActive
    return (partnerType && partnerTypeItems && (
      <>
        <Layout>
          <Layout.Item default="1/1">
            <DescriptionList grid>
              {partnerTypeItems &&
                partnerTypeItems?.map(
                  (item: string, index: number) => (
                    data?.[item]
                    ? (
                      <DescriptionList.Group key={item} termText={item}>
                        <DescriptionList.Detail>
                          {data?.[item]}
                        </DescriptionList.Detail>
                      </DescriptionList.Group>
                    ) : <></>
                  )
                )}
            </DescriptionList>
          </Layout.Item>
          <Layout.Item default="1/1" className='u-text-left'>
            <DescriptionList.Detail className="editLinkSummary">
              {t('summary:billingAddress')}
            </DescriptionList.Detail>
          </Layout.Item>
          <Layout.Item default="1/1">
            <DescriptionList grid>
              {(t(`summary:descriptionListVtiGis:addressItems`, {returnObjects: true}) as any)?.map(
                  (item: string) => (
                    data?.[item]
                    ? (
                    <DescriptionList.Group key={item} termText={item}>
                      <DescriptionList.Detail>
                        {data?.[item]}
                      </DescriptionList.Detail>
                    </DescriptionList.Group>
                    ) : <></>
                  )
                )}
            </DescriptionList>
          </Layout.Item>
          {isNotDeliveryTypeDealershop && 
          <>
          <Layout.Item default="1/1" className='u-text-left'>
            <DescriptionList.Detail className="editLinkSummary">
              {t('summary:descriptionListVtiGis:deliveryAddress:text')}
            </DescriptionList.Detail>
          </Layout.Item> 
          <Layout.Item default="1/1" className='u-text-left'>
            {isActiveDeliveryAddress
              ? (
                <DescriptionList grid>
                  {(t(`summary:descriptionListVtiGis:deliveryAddress:items`, {returnObjects: true}) as any)?.map(
                      (item: string) => (data?.[`deliveryAddress.${item}`] // only show if item exists
                        ? (
                          <DescriptionList.Group key={item} termText={item}>
                            <DescriptionList.Detail>
                              {data?.[`deliveryAddress.${item}`]}
                            </DescriptionList.Detail>
                          </DescriptionList.Group>
                        )
                      : <></>)
                    )}
                </DescriptionList>
              ) : (
                  t('summary:descriptionListVtiGis:deliveryAddress:no')
              )}
          </Layout.Item>
          </>
          }
        </Layout>
      </>
    ))
  }

  return (
    <>
      <ContentSection.ComponentWrapper className="u-mb-none">
        <Heading level={2} className="u-text-left">
          {t('summary:title')}
        </Heading>
        <Paragraph className="u-text-left">{t('summary:subtitle')}</Paragraph>
        <Layout>
          <Layout.Item default="1/1" className="u-text-left">
            <DescriptionList>
              <DescriptionList.Group>
                <DescriptionList.Detail
                  className="editLinkSummary"
                  action={
                    <Button
                      icon="semantic-edit"
                      link
                      simple
                      type="button"
                      aria-describedby={t('summary:editButtonTitle')}
                      onClick={() => {
                        const element = document.querySelector(
                          "input[name='customerData.firstName']"
                        ) as HTMLInputElement
                        element?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'center',
                        })
                        element?.focus()
                      }}
                    >
                      {t('summary:editButton')}
                    </Button>
                  }
                >
                  {isVtiGis ? t(`summary:personalDataTitle:VtiGis:${partnerType}`) : t('summary:personalDataTitle:text')}
                </DescriptionList.Detail>
              </DescriptionList.Group>
            </DescriptionList>
          </Layout.Item>
          <Layout.Item>
            {isVtiGis
              ? <DescriptionListVtiGis />
              : <DescriptionListBase />
            }
          </Layout.Item>
        </Layout>
      </ContentSection.ComponentWrapper>
    </>
  )
}
export default SummarySection
